import { AlertTriangle, Loader2, RefreshCw, Users } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { WithTooltip } from '@/components/with-tooltip';
import { cn } from '@/lib/utils';

export type AudienceTargetCountStatus = 'empty' | 'stale' | 'loading' | 'fresh';

interface AudienceTargetCountProps {
  status: AudienceTargetCountStatus;
  targetUsersCount: number | undefined;
  totalUserbaseCount: number;
  onRecalculate: () => void;
  locale: Locales;
  className?: string;
}

export function AudienceTargetCount({
  status,
  targetUsersCount,
  totalUserbaseCount,
  onRecalculate,
  locale,
  className,
}: AudienceTargetCountProps) {
  const { LL } = useI18nContext();
  const isStale = status === 'stale';
  const isLoading = status === 'loading';
  const showRefresh = status !== 'empty';
  const formattedTotal = totalUserbaseCount.toLocaleString(locale);

  const countDisplay =
    status === 'empty' || targetUsersCount === undefined
      ? '—'
      : targetUsersCount.toLocaleString(locale);

  const subtitle =
    status === 'empty'
      ? LL.audiences.create.targetCount.emptyHint()
      : targetUsersCount !== undefined
        ? LL.audiences.create.targetCount.totalUserbase({
            total: formattedTotal,
          })
        : null;

  return (
    <Card
      className={cn(
        'w-[300px] max-w-full self-start overflow-hidden',
        className
      )}
    >
      <CardHeader className="space-y-0 bg-secondary px-3 py-2">
        <CardTitle className="flex items-start justify-start gap-1.5 text-xs font-medium text-secondary-foreground">
          <Users
            className="mt-[2px] size-3.5 shrink-0"
            data-icon="inline-start"
          />
          <span
            className="overflow-hidden overflow-ellipsis whitespace-nowrap"
            title={LL.audiences.create.targetCount.label()}
          >
            {LL.audiences.create.targetCount.label()}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            {isLoading ? (
              <Skeleton className="h-7 w-20" />
            ) : (
              <p
                className={cn(
                  'text-lg font-semibold leading-tight tabular-nums',
                  status === 'empty' && 'text-muted-foreground'
                )}
              >
                {countDisplay}
              </p>
            )}
            {isStale && (
              <WithTooltip
                tooltip={LL.audiences.create.targetCount.staleHint()}
              >
                <span
                  className="inline-flex shrink-0 text-yellow-600 dark:text-yellow-500"
                  aria-label={LL.audiences.create.targetCount.staleHint()}
                >
                  <AlertTriangle className="size-4" />
                </span>
              </WithTooltip>
            )}
          </div>

          {showRefresh ? (
            <Button
              type="button"
              variant={isStale ? 'default' : 'ghost'}
              size="icon"
              className={cn(
                'size-8 shrink-0',
                isStale ? 'text-primary-foreground' : 'text-muted-foreground'
              )}
              onClick={onRecalculate}
              disabled={isLoading}
              aria-label={LL.audiences.create.targetCount.refreshAriaLabel()}
            >
              {isLoading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
            </Button>
          ) : (
            <span className="size-8 shrink-0" aria-hidden />
          )}
        </div>
        {subtitle && (
          <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}
